import { createContext, useContext, useReducer, useRef, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { fun, makeTemplate, getView } from "./utils";
import { useInitialize } from "./initializer";

const GridContext = createContext<any>(null);

const GridContextProvider = ({
    children,
    _grid,
    data,
    render,
    onRowCheck,
    onRowSelect,
    onRowClick,
    onCellClick,
    onPageChange,
    onSizeChange,
    onBeforeUpdate,
    defaultCheck,
}: any) => {
    _grid.current._render = render;
    _grid.current._onRowCheck = onRowCheck;
    _grid.current._onRowSelect = onRowSelect;
    _grid.current._onRowClick = onRowClick;
    _grid.current._onCellClick = onCellClick;
    _grid.current._onPageChange = onPageChange;
    _grid.current._onSizeChange = onSizeChange;
    _grid.current._onBeforeUpdate = onBeforeUpdate;
    _grid.current._defaultCheck = defaultCheck;

    if (!_grid.current._initialized) {
        if (Array.isArray(data?.content)) {
            _grid.current._data = data;
            const origin = data.content.map((_: any) => ({ ..._, __key: uuid(), __type: "origin" }));
            _grid.current._origin = origin;
            _grid.current._content = origin;
            getView(_grid);
            _grid.current._originTotalCount = _grid.current._totalCount;

            if (_grid.current._defaultCheck instanceof Function) {
                const checked = origin
                    .filter((item: any) => {
                        return _grid.current._defaultCheck(item);
                    })
                    .map(({ __key }: any) => {
                        return __key;
                    });
                _grid.current._checked = checked;
            }
        }
    }

    const init = useRef(false);
    const __t = data?.__t?.getTime();

    useEffect(() => {
        return () => {
            init.current = false;
            _grid.current._initialized = false;

            _grid.current._listRef = null;
            _grid.current._listInner = null;
            _grid.current._listOuter = null;
        };
    }, []);

    useEffect(() => {
        if (!init.current) {
            init.current = true;
            return;
        }
        if (!Array.isArray(data?.content)) return;
        if (data.content.length === 0 && _grid.current._content?.length === 0) return;
        _grid.current._setData(data);
    }, [__t]);

    const [value, dispatch] = useReducer(reducer, { _grid }, createInitialState);
    useInitialize({ _grid, dispatch });

    value._grid = _grid;
    value.test = value.test.length ? value.test : [{ __type: "empty" }];
    value.hasAdd = Boolean(value.add);
    value.hasDelete = Boolean(value.delete);
    value.hasIndex = Boolean(value.index);
    value.hasRadio = Boolean(value.radio);
    value.hasCheckbox = Boolean(Boolean(value.checkbox));
    value.hasPagination = Boolean(Boolean(value.pagination));
    value.hasGroup = Boolean(_grid.current._groupSchema);
    value.hasSort = Boolean(_grid.current._sortable);

    return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
};
const useGridContext = () => {
    return useContext(GridContext);
};
/**
 * .....................
 */

/**
 * ## State Initializer
 * @param param0
 * @returns
 */
const createInitialState = ({ _grid }: any) => {
    const obj: any = {};

    const headSchema = fun(_grid.current._head);
    const gridTemplateColumns = makeTemplate(headSchema);

    obj.headSchema = headSchema;
    obj.bodySchema = fun(_grid.current._body);
    obj.groupSchema = _grid.current._groupSchema && fun(_grid.current._groupSchema);
    obj.groupFootSchema = _grid.current._groupFootSchema && fun(_grid.current._groupFootSchema);
    obj.gridTemplateColumns = gridTemplateColumns;
    obj.test = _grid.current._view;
    obj.totalCount = _grid.current._totalCount || 0;
    obj.totalItemCount = _grid.current._totalItemCount || 0;
    obj.editingRow = [];
    obj.page = _grid.current._page;
    obj.size = _grid.current._size;
    obj.sort = _grid.current._sort;
    obj.checked = _grid.current._checked;
    obj.selectedRow = _grid.current._selectedRow;
    obj.selectedCel = _grid.current._selectedCel;
    obj.headHeight = _grid.current._headHeight || 0;

    obj.add = _grid.current._add;
    obj.delete = _grid.current._delete;
    obj.index = _grid.current._index;
    obj.radio = _grid.current._radio;
    obj.checkbox = _grid.current._checkbox;

    obj.edit = _grid.current._edit;
    obj.height = _grid.current._height;
    obj.exportExcel = _grid.current._exportExcel;
    obj.exportSheet = _grid.current._exportSheet;
    obj.importExcel = _grid.current._importExcel;
    obj.openCompare = _grid.current._openCompare;
    obj.pagination = _grid.current._pagination;

    obj.refreshCount = 0;

    return obj;
};

/**
 * ## Reducer
 * @param state
 * @param action
 * @returns
 */
const reducer = (prev: any, action: any) => {
    const next = { ...prev };
    switch (action.type) {
        /* Set data  */
        case "setData": {
            const { _grid } = action.payload;
            next.page = _grid.current._page;
            next.size = _grid.current._size;
            next.test = _grid.current._view;
            next.totalCount = _grid.current._totalCount;
            next.totalItemCount = _grid.current._totalItemCount;
            next.checked = _grid.current._checked;
            next.selectedRow = null;
            next.selectedCel = null;
            return next;
        }
        /* Set data to origin */
        case "resetData": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            next.totalCount = _grid.current._totalCount;
            next.totalItemCount = _grid.current._totalItemCount;
            return next;
        }
        /* Clear data */
        case "clearData": {
            next.test = [];
            next.totalCount = 0;
            next.totalItemCount = 0;
            next.page = 0;
            next.size = 10;
            return next;
        }
        /* Add row  */
        case "add": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            next.totalCount = _grid.current._totalCount;
            next.totalItemCount = _grid.current._totalItemCount;
            return next;
        }
        /* Delete row */
        case "delete": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            next.totalCount = _grid.current._totalCount;
            next.totalItemCount = _grid.current._totalItemCount;
            next.checked = _grid.current._checked;
            next.selectedCel = _grid.current._selectedCel;
            next.selectedRow = _grid.current._selectedRow;
            return next;
        }
        /* Update row */
        case "update": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            return next;
        }
        /* Reset row */
        case "resetRow": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            return next;
        }
        /* Sort */
        case "sort": {
            const { _grid } = action.payload;
            next.sort = _grid.current._sort;
            next.test = _grid.current._view;
            return next;
        }
        /* Group Toggle */
        case "group": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            next.totalItemCount = _grid.current._totalItemCount;
            return next;
        }
        /* Set edit */
        case "setEdit": {
            const { _grid } = action.payload;
            next.bodySchema = fun(_grid.current._body);
            next.editingRow = _grid.current._editingRow;
            return next;
        }
        /* Set show */
        case "setShow": {
            const { _grid } = action.payload;
            const headSchema = fun(_grid.current._head);
            const bodySchema = fun(_grid.current._body);
            const gridTemplateColumns = makeTemplate(headSchema);
            next.headSchema = headSchema;
            next.bodySchema = bodySchema;
            next.gridTemplateColumns = gridTemplateColumns;

            return next;
        }
        /* Set Option */
        case "setOption": {
            const { _grid, target } = action.payload;
            next.bodySchema = fun(_grid.current._body);
            next[target] = _grid.current[`_${target}`];
            return next;
        }
        /* Handle Select Cell  */
        case "handleClickCel": {
            const { _grid } = action.payload;
            next.selectedCel = _grid.current._selectedCel;
            return next;
        }
        /* Handle Select Row (Radio) */
        case "handleSelect": {
            const { _grid } = action.payload;
            next.selectedRow = _grid.current._selectedRow;
            return next;
        }
        /* Handle Check Row (Checkbox) */
        case "handleCheck": {
            const { _grid } = action.payload;
            next.checked = _grid.current._checked;
            return next;
        }
        /* Handle Check All Row (Checkbox) */
        case "handleCheckAll": {
            const { _grid } = action.payload;
            next.checked = _grid.current._checked;
            return next;
        }
        /* unCheck */
        case "unCheck": {
            const { _grid } = action.payload;
            next.checked = _grid.current._checked;
            return next;
        }
        /* unCheck */
        case "unSelectRow": {
            const { _grid } = action.payload;
            next.selectedRow = _grid.current._selectedRow;
            return next;
        }
        /* unCheck */
        case "unSelectCel": {
            const { _grid } = action.payload;
            next.selectedCel = _grid.current._selectedCel;
            return next;
        }
        /* Handle Change Page */
        case "handleChangePage": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            next.page = _grid.current._page;
            next.checked = _grid.current._checked;
            next.selectedRow = _grid.current._selectedRow;
            next.selectedCel = _grid.current._selectedCel;
            return next;
        }
        /* Handle Change Size */
        case "handleChangeSize": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            next.size = _grid.current._size;
            next.page = 0;
            next.checked = _grid.current._checked;
            next.selectedRow = _grid.current._selectedRow;
            next.selectedCel = _grid.current._selectedCel;
            return next;
        }
        /* Readjust Row Height */
        case "readjustHeight": {
            const { _grid } = action.payload;
            next.height = _grid.current._height + 1;
            return next;
        }

        /* Up Row */
        case "upRow": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            return next;
        }

        /* Down Row */
        case "downRow": {
            const { _grid } = action.payload;
            next.test = _grid.current._view;
            return next;
        }

        /* Set Schema */
        case "setSchema": {
            const { _grid } = action.payload;
            return createInitialState({ _grid });
        }

        /* Set Head Schema */
        case "setHead": {
            const { _grid } = action.payload;
            const headSchema = fun(_grid.current._head);
            const gridTemplateColumns = makeTemplate(headSchema);
            next.headSchema = headSchema;
            next.gridTemplateColumns = gridTemplateColumns;
            return next;
        }
        /* Set Body Schema */
        case "setBody": {
            const { _grid } = action.payload;
            const bodySchema = fun(_grid.current._body);
            next.bodySchema = bodySchema;
            return next;
        }
        /* Set Group Schema */
        case "setGroup": {
            const { _grid } = action.payload;
            next.groupSchema = fun(_grid.current._groupSchema);
            next.test = _grid.current._view;

            return next;
        }
        /* Set GroupFoot Schema */
        case "setGroupFoot": {
            const { _grid } = action.payload;
            next.groupFootSchema = fun(_grid.current._groupFootSchema);
            next.test = _grid.current._view;
            return next;
        }

        /* Set Head Height */
        case "setHeadHeight": {
            const { _grid } = action.payload;
            next.headHeight = _grid.current._headHeight;
            return next;
        }

        /* Refresh */
        case "refresh": {
            next.refreshCount += 1;
            return next;
        }

        default:
            return next;
    }
};

export { GridContextProvider, useGridContext, createInitialState, reducer };
